import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { recruitmentTheme, themeClasses } from '../styles/theme';
import { cn, formatDate, formatDateTime } from '../utils';
import { LocalStorageManager } from '../utils/localStorage';
import { clearAllCVDraftsFromLocalStorage } from '../utils/clearCVDrafts';
import { comprehensiveCVDraftCleanup } from '../utils/cleanupCVDrafts';
import { addToRecycleBin } from '../utils/recycleBin';
import { useNavigate } from 'react-router-dom';
import { 
  subscribeToDocumentsByCategory,
  subscribeToCVDrafts,
  addDocument,
  deleteDocument as deleteDocumentFromFirebase,
  addCVDraft as addCVDraftToFirebase,
  migrateDocumentsToFirebase,
  deleteAllCVDrafts,
  Document as FirebaseDocument,
  CVDraft as FirebaseCVDraft
} from '../services/documentService';
import { useNotifications } from '../contexts/NotificationContext';
import {
  FileText,
  Upload,
  Download,
  Trash2,
  Eye,
  File,
  FileImage,
  FileVideo,
  FileAudio,
  Folder,
  Shield,
  Award,
  Building2,
  Files,
  Megaphone,
  BarChart3,
  Edit,
  Check,
  X,
  Square,
  CheckSquare,
  Camera
} from 'lucide-react';

// Types
interface DocFile {
  id: string;
  name: string;
  type: string;
  size: number;
  uploadedAt: string;
  uploadedBy: string;
  url: string;
  category: string;
  description?: string;
}

interface DocumentSection {
  title: string;
  description: string;
  icon: React.ReactNode;
  category: string;
  documents: DocFile[];
}

// Mock data for demonstration
const mockDocuments: DocFile[] = [
  // Medical Reports
  {
    id: 'med-1',
    name: 'John Doe Medical Examination Report.pdf',
    type: 'application/pdf',
    size: 456789,
    uploadedAt: '2024-01-20T11:00:00Z',
    uploadedBy: 'Medical Officer',
    url: '#',
    category: 'medical-examinations',
    description: 'Pre-employment medical examination for John Doe'
  },
  {
    id: 'med-2',
    name: 'Health Certificate - Jane Smith.pdf',
    type: 'application/pdf',
    size: 234567,
    uploadedAt: '2024-01-18T09:30:00Z',
    uploadedBy: 'Health Department',
    url: '#',
    category: 'health-certificates',
    description: 'Health clearance certificate for Jane Smith'
  },
  {
    id: 'med-3',
    name: 'COVID-19 Vaccination Record.jpg',
    type: 'image/jpeg',
    size: 567890,
    uploadedAt: '2024-01-15T14:20:00Z',
    uploadedBy: 'Healthcare Provider',
    url: '#',
    category: 'vaccination-records',
    description: 'COVID-19 vaccination certificate'
  },
  // Contracts
  {
    id: '1',
    name: 'John Doe Employment Contract.pdf',
    type: 'application/pdf',
    size: 245678,
    uploadedAt: '2024-01-15T10:30:00Z',
    uploadedBy: 'Admin User',
    url: '#',
    category: 'candidate-contracts',
    description: 'Employment contract for John Doe'
  },
  {
    id: '2',
    name: 'ABC Company Service Agreement.docx',
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    size: 189234,
    uploadedAt: '2024-01-10T14:20:00Z',
    uploadedBy: 'HR Manager',
    url: '#',
    category: 'company-contracts',
    description: 'Service agreement with ABC Company'
  },
  {
    id: '3',
    name: 'Employee Handbook 2024.pdf',
    type: 'application/pdf',
    size: 567890,
    uploadedAt: '2024-01-05T09:15:00Z',
    uploadedBy: 'HR Department',
    url: '#',
    category: 'employee-contracts',
    description: 'Updated employee handbook for 2024'
  },
  // Licenses & Certifications
  {
    id: '4',
    name: 'Business License 2024.jpg',
    type: 'image/jpeg',
    size: 1234567,
    uploadedAt: '2024-01-01T08:00:00Z',
    uploadedBy: 'Legal Department',
    url: '#',
    category: 'business-licenses',
    description: 'Business operating license for 2024'
  },
  {
    id: '5',
    name: 'ISO 9001 Certificate.pdf',
    type: 'application/pdf',
    size: 345678,
    uploadedAt: '2023-12-20T11:30:00Z',
    uploadedBy: 'Quality Assurance',
    url: '#',
    category: 'professional-certifications',
    description: 'ISO 9001:2015 certification'
  },
  {
    id: '6',
    name: 'Data Protection Registration.pdf',
    type: 'application/pdf',
    size: 234567,
    uploadedAt: '2023-12-15T13:45:00Z',
    uploadedBy: 'Compliance Officer',
    url: '#',
    category: 'regulatory-documents',
    description: 'GDPR compliance registration'
  }
];

// Component
const DocumentsPage: React.FC = () => {
  const { showNotification } = useNotifications();
  const navigate = useNavigate();
  const [firebaseDocuments, setFirebaseDocuments] = useState<FirebaseDocument[]>([]);
  const [firebaseCVDrafts, setFirebaseCVDrafts] = useState<FirebaseCVDraft[]>([]);
  const [firebaseLoading, setFirebaseLoading] = useState(true);
  
  // Helper functions to ensure TypeScript recognizes the methods
  const deleteCVDraft = (id: string) => {
    return LocalStorageManager.deleteCVDraft(id);
  };
  
  const getCVDraftById = (id: string) => {
    // First check Firebase drafts
    const firebaseDraft = firebaseCVDrafts.find(draft => draft.id === id);
    if (firebaseDraft) {
      return firebaseDraft;
    }
    // Fallback to localStorage
    return LocalStorageManager.getCVDraftById(id);
  };
  
  const getCVDrafts = React.useCallback(() => {
    return LocalStorageManager.getCVDrafts();
  }, []);
  const [activeTab, setActiveTab] = useState<'medical-reports' | 'contracts' | 'licenses' | 'cvs' | 'marketing' | 'reports'>('cvs');
  const [documents, setDocuments] = useState<DocFile[]>(() => {
    // Load documents from localStorage on initial mount
    const uploadedDocs = JSON.parse(localStorage.getItem('uploadedDocuments') || '[]');
    const deletedMockDocs = JSON.parse(localStorage.getItem('deletedMockDocuments') || '[]');
    const activeMockDocuments = mockDocuments.filter(mockDoc => !deletedMockDocs.includes(mockDoc.id));
    
    // CV drafts will be loaded from Firebase subscription instead
    return [...activeMockDocuments, ...uploadedDocs];
  });
  const [selectedDocument, setSelectedDocument] = useState<DocFile | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingDocument, setEditingDocument] = useState<{id: string, name: string} | null>(null);
  const [previewDocument, setPreviewDocument] = useState<DocFile | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Handle keyboard events for preview modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showPreviewModal) {
        setShowPreviewModal(false);
        setPreviewDocument(null);
      }
    };

    if (showPreviewModal) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [showPreviewModal]);

  // Load documents function (moved outside useEffect so it can be called elsewhere)
  const loadDocuments = React.useCallback(() => {
      const uploadedDocs = JSON.parse(localStorage.getItem('uploadedDocuments') || '[]');
      const deletedMockDocs = JSON.parse(localStorage.getItem('deletedMockDocuments') || '[]');
      const activeMockDocuments = mockDocuments.filter(mockDoc => !deletedMockDocs.includes(mockDoc.id));
      
      // Don't load CV drafts here - they come from Firebase subscription
      const allDocuments = [...activeMockDocuments, ...uploadedDocs];
      
      // Update documents but preserve CV drafts from Firebase
      setDocuments(prevDocs => {
        const cvDrafts = prevDocs.filter(doc => doc.category === 'cv-drafts');
        return [...allDocuments, ...cvDrafts];
      });
    }, []);

  // Load documents from localStorage and sync with storage changes
  React.useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'uploadedDocuments' || e.key === 'deletedMockDocuments' || e.key === 'recruitment_crm_cv_drafts') {
        loadDocuments();
      }
    };

    const handleCVDraftsUpdate = (e: CustomEvent) => {
      loadDocuments();
    };

    const handleFocus = () => {
      loadDocuments();
    };

    const handleDocumentsPageAction = (e: CustomEvent) => {
      if (e.detail?.action === 'openUploadedCVs') {
        // Set the CVs tab as active
        setActiveTab('cvs');
        // Scroll to the Uploaded CVs section after a short delay
        setTimeout(() => {
          const uploadedCVsSection = document.querySelector('[data-section="uploaded-cvs"]');
          if (uploadedCVsSection) {
            uploadedCVsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Find and click the upload button in the Uploaded CVs section
            const uploadButton = uploadedCVsSection.querySelector('button[data-upload-button="uploaded-cvs"]');
            if (uploadButton) {
              (uploadButton as HTMLButtonElement).focus();
              // Highlight the button temporarily
              uploadButton.classList.add('ring-2', 'ring-[#8b6914]', 'ring-opacity-50');
              setTimeout(() => {
                uploadButton.classList.remove('ring-2', 'ring-[#8b6914]', 'ring-opacity-50');
              }, 2000);
            }
          }
        }, 200);
      }
    };

    loadDocuments();

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('cvDraftsUpdated', handleCVDraftsUpdate as EventListener);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('documentsPageAction', handleDocumentsPageAction as EventListener);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cvDraftsUpdated', handleCVDraftsUpdate as EventListener);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('documentsPageAction', handleDocumentsPageAction as EventListener);
    };
  }, [loadDocuments]);

  // Firebase subscriptions
  useEffect(() => {
    console.log('📄 Documents: Setting up Firebase sync...');
    setFirebaseLoading(true);
    
    // Migrate existing data to Firebase on first load
    const migrateData = async () => {
      try {
        await migrateDocumentsToFirebase();
      } catch (error) {
        console.error('Failed to migrate documents:', error);
      }
    };
    
    migrateData();
    
    const unsubscribers: (() => void)[] = [];
    
    // Subscribe to CV drafts
    const unsubscribeCVDrafts = subscribeToCVDrafts((drafts) => {
      console.log('📋 Documents: Received', drafts.length, 'CV drafts from Firebase');
      setFirebaseCVDrafts(drafts);
      
      // Convert Firebase drafts to DocFile format with unique IDs
      const cvDraftDocuments: DocFile[] = drafts
        .filter(draft => draft.id) // Ensure draft has a unique ID from Firebase
        .map((draft) => ({
          id: draft.id!, // Non-null assertion as we've filtered
          name: draft.name || 'Unnamed CV Draft',
          type: 'cv-draft',
          size: draft.size || JSON.stringify(draft).length,
          uploadedAt: draft.uploadedAt || new Date().toISOString(),
          uploadedBy: draft.uploadedBy || 'CV Builder',
          url: draft.url || `#cv-draft-${draft.id}`,
          category: 'cv-drafts',
          description: draft.description || `CV draft for ${draft.candidateName || 'Unknown Candidate'}`
        }));
      
      // Update documents with Firebase drafts - clear old CV drafts first
      setDocuments(prevDocs => {
        const nonCVDraftDocs = prevDocs.filter(doc => doc.category !== 'cv-drafts');
        return [...nonCVDraftDocs, ...cvDraftDocuments];
      });
      
      setFirebaseLoading(false);
    }, (error) => {
      console.error('❌ Documents: Firebase CV drafts subscription error:', error);
      setFirebaseLoading(false);
    });
    
    unsubscribers.push(unsubscribeCVDrafts);
    
    // Subscribe to each document category (including all sub-categories used in sections)
    const categories = [
      // Medical categories
      'medical-examinations', 'health-certificates', 'vaccination-records', 'other-medical',
      // Contract categories  
      'candidate-contracts', 'company-contracts', 'employee-contracts', 'other-contracts',
      // License categories
      'business-licenses', 'professional-certifications', 'regulatory-documents', 'other-documents',
      // CV categories (cv-drafts handled separately by subscribeToCVDrafts)
      'cv-builder-cvs', 'uploaded-cvs',
      // Marketing categories
      'marketing-materials', 'advertisements', 'brand-assets', 'social-media',
      // Report categories
      'financial-reports', 'performance-reports', 'compliance-reports', 'analytics-reports'
    ];
    
    categories.forEach(category => {
      const unsubscribe = subscribeToDocumentsByCategory(category, (docs) => {
        console.log(`📄 Documents: Received ${docs.length} ${category} documents from Firebase`);
        
        // Convert Firebase documents to DocFile format
        const categoryDocuments: DocFile[] = docs.map((doc) => ({
          id: doc.id || '',
          name: doc.name,
          type: doc.type,
          size: doc.size,
          uploadedAt: doc.uploadedAt,
          uploadedBy: doc.uploadedBy,
          url: doc.url,
          category: doc.category,
          description: doc.description
        }));
        
        // Update documents with Firebase documents for this category
        setDocuments(prevDocs => {
          const otherDocs = prevDocs.filter(doc => doc.category !== category);
          return [...otherDocs, ...categoryDocuments];
        });
        
        setFirebaseLoading(false);
      }, (error) => {
        console.error(`❌ Documents: Firebase ${category} subscription error:`, error);
        setFirebaseLoading(false);
      });
      
      unsubscribers.push(unsubscribe);
    });
    
    return () => {
      console.log('📄 Documents: Cleaning up Firebase subscriptions...');
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, []);

  // Document sections configuration
  const medicalReportsSections: DocumentSection[] = [
    {
      title: 'Medical Examination Reports',
      description: 'Pre-employment medical examination reports and health certificates',
      icon: <FileText className="w-5 h-5" />,
      category: 'medical-examinations',
      documents: documents.filter(doc => doc.category === 'medical-examinations')
    },
    {
      title: 'Health Certificates',
      description: 'Medical clearance certificates and health documentation',
      icon: <Shield className="w-5 h-5" />,
      category: 'health-certificates',
      documents: documents.filter(doc => doc.category === 'health-certificates')
    },
    {
      title: 'Vaccination Records',
      description: 'Vaccination certificates and immunization records',
      icon: <Award className="w-5 h-5" />,
      category: 'vaccination-records',
      documents: documents.filter(doc => doc.category === 'vaccination-records')
    },
    {
      title: 'Other Medical Documents',
      description: 'Additional medical reports and health-related documents',
      icon: <Folder className="w-5 h-5" />,
      category: 'other-medical',
      documents: documents.filter(doc => doc.category === 'other-medical')
    }
  ];

  const contractSections: DocumentSection[] = [
    {
      title: 'Candidate Contracts',
      description: 'Employment contracts and agreements for candidates',
      icon: <FileText className="w-5 h-5" />,
      category: 'candidate-contracts',
      documents: documents.filter(doc => doc.category === 'candidate-contracts')
    },
    {
      title: 'Company Contracts',
      description: 'Service agreements and contracts with client companies',
      icon: <Building2 className="w-5 h-5" />,
      category: 'company-contracts',
      documents: documents.filter(doc => doc.category === 'company-contracts')
    },
    {
      title: 'Employee Contracts',
      description: 'Internal employee contracts and HR documents',
      icon: <Files className="w-5 h-5" />,
      category: 'employee-contracts',
      documents: documents.filter(doc => doc.category === 'employee-contracts')
    },
    {
      title: 'Other Contracts',
      description: 'Miscellaneous contracts and legal agreements',
      icon: <Folder className="w-5 h-5" />,
      category: 'other-contracts',
      documents: documents.filter(doc => doc.category === 'other-contracts')
    }
  ];

  const licenseSections: DocumentSection[] = [
    {
      title: 'Business Licenses',
      description: 'Business operation licenses and permits',
      icon: <Shield className="w-5 h-5" />,
      category: 'business-licenses',
      documents: documents.filter(doc => doc.category === 'business-licenses')
    },
    {
      title: 'Professional Certifications',
      description: 'Industry certifications and quality standards',
      icon: <Award className="w-5 h-5" />,
      category: 'professional-certifications',
      documents: documents.filter(doc => doc.category === 'professional-certifications')
    },
    {
      title: 'Regulatory Documents',
      description: 'Compliance and regulatory documentation',
      icon: <FileText className="w-5 h-5" />,
      category: 'regulatory-documents',
      documents: documents.filter(doc => doc.category === 'regulatory-documents')
    },
    {
      title: 'Other Documents',
      description: 'Additional licenses and certifications',
      icon: <Folder className="w-5 h-5" />,
      category: 'other-documents',
      documents: documents.filter(doc => doc.category === 'other-documents')
    }
  ];

  const cvSections: DocumentSection[] = [
    {
      title: 'CV Builder CVs',
      description: 'CVs created and saved from the CV Builder application',
      icon: <FileText className="w-5 h-5" />,
      category: 'cv-builder-cvs',
      documents: documents.filter(doc => doc.category === 'cv-builder-cvs')
    },
    {
      title: 'Uploaded CVs',
      description: 'CVs uploaded manually by users',
      icon: <Upload className="w-5 h-5" />,
      category: 'uploaded-cvs',
      documents: documents.filter(doc => doc.category === 'uploaded-cvs')
    },
    {
      title: 'CV Drafts',
      description: 'Draft CVs that are work in progress',
      icon: <Files className="w-5 h-5" />,
      category: 'cv-drafts',
      documents: documents.filter(doc => doc.category === 'cv-drafts')
    }
  ];

  const marketingSections: DocumentSection[] = [
    {
      title: 'Marketing Materials',
      description: 'Brochures, flyers, and promotional materials',
      icon: <Megaphone className="w-5 h-5" />,
      category: 'marketing-materials',
      documents: documents.filter(doc => doc.category === 'marketing-materials')
    },
    {
      title: 'Advertisements',
      description: 'Job advertisements and recruitment campaigns',
      icon: <FileText className="w-5 h-5" />,
      category: 'advertisements',
      documents: documents.filter(doc => doc.category === 'advertisements')
    },
    {
      title: 'Brand Assets',
      description: 'Logos, brand guidelines, and visual identity materials',
      icon: <FileImage className="w-5 h-5" />,
      category: 'brand-assets',
      documents: documents.filter(doc => doc.category === 'brand-assets')
    },
    {
      title: 'Social Media Content',
      description: 'Social media posts, campaigns, and content calendars',
      icon: <Files className="w-5 h-5" />,
      category: 'social-media',
      documents: documents.filter(doc => doc.category === 'social-media')
    }
  ];

  const reportsSections: DocumentSection[] = [
    {
      title: 'Financial Reports',
      description: 'Monthly, quarterly, and annual financial reports',
      icon: <BarChart3 className="w-5 h-5" />,
      category: 'financial-reports',
      documents: documents.filter(doc => doc.category === 'financial-reports')
    },
    {
      title: 'Performance Reports',
      description: 'Staff performance and business metrics reports',
      icon: <FileText className="w-5 h-5" />,
      category: 'performance-reports',
      documents: documents.filter(doc => doc.category === 'performance-reports')
    },
    {
      title: 'Compliance Reports',
      description: 'Regulatory compliance and audit reports',
      icon: <Shield className="w-5 h-5" />,
      category: 'compliance-reports',
      documents: documents.filter(doc => doc.category === 'compliance-reports')
    },
    {
      title: 'Analytics Reports',
      description: 'Data analytics and business intelligence reports',
      icon: <BarChart3 className="w-5 h-5" />,
      category: 'analytics-reports',
      documents: documents.filter(doc => doc.category === 'analytics-reports')
    }
  ];

  // Helper functions
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string): React.ReactNode => {
    if (!type) return <File className="w-5 h-5 text-gray-500" />;
    
    const fileType = type.toLowerCase();
    if (fileType.includes('cv-draft')) return <Files className="w-5 h-5 text-[#8b6914]" />;
    if (fileType.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (fileType.includes('word') || fileType.includes('document')) return <FileText className="w-5 h-5 text-blue-500" />;
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return <FileText className="w-5 h-5 text-green-500" />;
    if (fileType.includes('image')) return <FileImage className="w-5 h-5 text-purple-500" />;
    if (fileType.includes('video')) return <FileVideo className="w-5 h-5 text-orange-500" />;
    if (fileType.includes('audio')) return <FileAudio className="w-5 h-5 text-pink-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  const handleFileUpload = async (category: string) => {
    // Create a file input and trigger it
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '*/*'; // Accept all file types
    input.multiple = true;
    
    input.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files) {
        for (const file of Array.from(files)) {
          try {
            // Create a new document entry
            const newDocument: Omit<FirebaseDocument, 'id'> = {
              name: file.name,
              type: file.type || 'application/octet-stream',
              size: file.size,
              uploadedAt: new Date().toISOString(),
              uploadedBy: 'Current User',
              url: URL.createObjectURL(file), // In production, this would be uploaded to a server
              category: category
            };
            
            // Add to Firebase
            const docId = await addDocument(newDocument);
            console.log('✅ Document uploaded to Firebase:', file.name, 'with ID:', docId);
            
            showNotification({
              type: 'success',
              title: 'Document Uploaded',
              message: `${file.name} has been uploaded successfully!`,
              duration: 3000
            });
          } catch (error) {
            console.error('Failed to upload document:', error);
            showNotification({
              type: 'error',
              title: 'Upload Failed',
              message: `Failed to upload ${file.name}. Please try again.`,
              duration: 5000
            });
          }
        }
      }
    };
    
    input.click();
  };

  const handleCameraCapture = async (category: string) => {
    try {
      // Create a file input with camera capture
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.capture = 'environment'; // Use back camera by default
      input.multiple = true; // Allow multiple photos
      
      input.onchange = async (e) => {
        const files = (e.target as HTMLInputElement).files;
        if (files) {
          for (const file of Array.from(files)) {
            try {
              // Create a new document entry
              const newDocument: Omit<FirebaseDocument, 'id'> = {
                name: file.name,
                type: file.type || 'image/jpeg',
                size: file.size,
                uploadedAt: new Date().toISOString(),
                uploadedBy: 'Current User',
                url: URL.createObjectURL(file),
                category: category
              };
              
              // Add to Firebase
              const docId = await addDocument(newDocument);
              console.log('✅ Photo captured and uploaded to Firebase:', file.name, 'with ID:', docId);
              
              showNotification({
                type: 'success',
                title: 'Photo Captured',
                message: `Photo "${file.name}" has been captured and uploaded successfully!`,
                duration: 3000
              });
            } catch (error) {
              console.error('Failed to upload captured photo:', error);
              showNotification({
                type: 'error',
                title: 'Upload Failed',
                message: `Failed to upload captured photo. Please try again.`,
                duration: 5000
              });
            }
          }
        }
      };
      
      input.click();
    } catch (error) {
      console.error('Camera capture error:', error);
      showNotification({
        type: 'error',
        title: 'Camera Error',
        message: 'Camera capture failed. Please try using the file upload option.',
        duration: 5000
      });
    }
  };

  // Checkbox selection handlers
  const handleSelectDocument = (documentId: string) => {
    const newSelected = new Set(selectedDocuments);
    if (newSelected.has(documentId)) {
      newSelected.delete(documentId);
    } else {
      newSelected.add(documentId);
    }
    setSelectedDocuments(newSelected);
  };

  const handleSelectAll = (sectionDocuments: DocFile[]) => {
    const sectionIds = sectionDocuments.map(doc => doc.id);
    const newSelected = new Set(selectedDocuments);
    const allSelected = sectionIds.every(id => newSelected.has(id));
    
    if (allSelected) {
      // Deselect all in this section
      sectionIds.forEach(id => newSelected.delete(id));
    } else {
      // Select all in this section
      sectionIds.forEach(id => newSelected.add(id));
    }
    setSelectedDocuments(newSelected);
  };

  const clearSelection = () => {
    setSelectedDocuments(new Set());
    setIsEditMode(false);
    setEditingDocument(null);
  };

  // Edit/Rename functionality
  const handleStartEdit = (doc: DocFile) => {
    setEditingDocument({ id: doc.id, name: doc.name });
  };

  const handleSaveEdit = () => {
    if (!editingDocument) return;

    const updatedDocuments = documents.map(doc => {
      if (doc.id === editingDocument.id) {
        return { ...doc, name: editingDocument.name };
      }
      return doc;
    });

    // Update localStorage for uploaded documents
    const uploadedDocs = JSON.parse(localStorage.getItem('uploadedDocuments') || '[]');
    const updatedUploadedDocs = uploadedDocs.map((doc: DocFile) => {
      if (doc.id === editingDocument.id) {
        return { ...doc, name: editingDocument.name };
      }
      return doc;
    });
    localStorage.setItem('uploadedDocuments', JSON.stringify(updatedUploadedDocs));

    // Update CV drafts if it's a CV draft
    const doc = documents.find(d => d.id === editingDocument.id);
    if (doc && doc.category === 'cv-drafts') {
      const draftId = doc.url.replace('#cv-draft-', '');
      const draft = getCVDraftById(draftId);
      if (draft) {
        draft.name = editingDocument.name;
        LocalStorageManager.addCVDraft(draft);
      }
    }

    setDocuments(updatedDocuments);
    setEditingDocument(null);
  };

  const handleCancelEdit = () => {
    setEditingDocument(null);
  };

  // Bulk delete functionality
  const handleBulkDelete = () => {
    if (selectedDocuments.size === 0) return;

    const selectedCount = selectedDocuments.size;
    if (window.confirm(`Are you sure you want to delete ${selectedCount} selected document(s)? They will be moved to the recycle bin.`)) {
      let successCount = 0;
      let errorCount = 0;
      
      selectedDocuments.forEach(documentId => {
        const doc = documents.find(d => d.id === documentId);
        if (doc) {
          try {
            // Add to recycle bin
            addToRecycleBin({
              type: 'document',
              name: doc.name,
              details: `${doc.category} | Size: ${formatFileSize(doc.size)} | Uploaded by: ${doc.uploadedBy}`,
              deletedBy: 'Current User',
              data: doc
            });
            successCount++;

            // Delete from Firebase for CV drafts
            if (doc.category === 'cv-drafts') {
              console.log('🗑️ Deleting CV draft from Firebase:', doc.id);
              deleteDocumentFromFirebase(doc.id).catch(err => {
                console.error('Failed to delete CV draft from Firebase:', err);
              });
              
              // Also remove from localStorage if it exists
              const draftId = doc.url.replace('#cv-draft-', '');
              if (draftId && LocalStorageManager.deleteCVDraft) {
                LocalStorageManager.deleteCVDraft(draftId);
              }
            }
          } catch (error) {
            console.error('Failed to add document to recycle bin:', error);
            errorCount++;
            // Continue with deletion even if recycle bin fails
          }
        }
      });

      // Update uploaded documents
      const existingUploaded = JSON.parse(localStorage.getItem('uploadedDocuments') || '[]');
      const updatedUploaded = existingUploaded.filter((doc: DocFile) => !selectedDocuments.has(doc.id));
      localStorage.setItem('uploadedDocuments', JSON.stringify(updatedUploaded));

      // Update deleted mock documents
      const deletedMockDocs = JSON.parse(localStorage.getItem('deletedMockDocuments') || '[]');
      selectedDocuments.forEach(docId => {
        const isMockDocument = mockDocuments.some(mockDoc => mockDoc.id === docId);
        if (isMockDocument && !deletedMockDocs.includes(docId)) {
          deletedMockDocs.push(docId);
        }
      });
      localStorage.setItem('deletedMockDocuments', JSON.stringify(deletedMockDocs));

      // Reload documents
      loadDocuments();
      clearSelection();

      if (errorCount > 0) {
        alert(`${successCount} document(s) deleted successfully. ${errorCount} document(s) could not be moved to recycle bin due to storage limitations but were permanently deleted.`);
      } else {
        alert(`${selectedCount} document(s) moved to recycle bin!`);
      }
    }
  };

  const handleDelete = async (documentId: string) => {
    if (window.confirm('Are you sure you want to delete this document? It will be moved to the recycle bin.')) {
      // Find the document first
      const doc = documents.find(d => d.id === documentId);
      console.log('🗑️ Attempting to delete document:', doc);
      console.log('📄 Documents before deletion:', documents.length);
      
      if (doc) {
        let addedToRecycleBin = false;
        try {
          // Add to recycle bin
          addToRecycleBin({
            type: 'document',
            name: doc.name,
            details: `${doc.category} | Size: ${formatFileSize(doc.size)} | Uploaded by: ${doc.uploadedBy}`,
            deletedBy: 'Current User',
            data: doc
          });
          console.log('♻️ Document added to recycle bin');
          addedToRecycleBin = true;
        } catch (error) {
          console.error('Failed to add document to recycle bin:', error);
          addedToRecycleBin = false;
        }

        try {
          // Delete from Firebase
          await deleteDocumentFromFirebase(documentId);
          console.log('✅ Document deleted from Firebase');
          
          // For CV drafts, also clean up localStorage
          if (doc.category === 'cv-drafts') {
            const draftId = doc.url.replace('#cv-draft-', '');
            if (draftId && LocalStorageManager.deleteCVDraft) {
              try {
                LocalStorageManager.deleteCVDraft(draftId);
                console.log('✅ CV draft removed from localStorage');
              } catch (err) {
                console.error('Failed to delete CV draft from localStorage:', err);
              }
            }
          }
          
          if (addedToRecycleBin) {
            showNotification({
              type: 'success',
              title: 'Document Deleted',
              message: 'Document moved to recycle bin!',
              duration: 5000
            });
          } else {
            showNotification({
              type: 'warning',
              title: 'Document Deleted',
              message: 'Document deleted permanently (could not be moved to recycle bin due to storage limitations).',
              duration: 5000
            });
          }
        } catch (error) {
          console.error('Failed to delete document from Firebase:', error);
          showNotification({
            type: 'error',
            title: 'Delete Failed',
            message: 'Failed to delete document. Please try again.',
            duration: 5000
          });
        }
      } else {
        console.error('❌ Document not found for deletion:', documentId);
        alert('Document not found.');
      }
    }
  };

  const handlePreview = (doc: DocFile) => {
    setSelectedDocument(doc);
    
    // Special handling for CV drafts
    if (doc.category === 'cv-drafts') {
      // Use the document ID directly since CV drafts might have url as just '#'
      const draftId = doc.url.startsWith('#cv-draft-') ? doc.url.replace('#cv-draft-', '') : doc.id;
      
      console.log('🔍 Preview CV Draft - Document:', doc);
      console.log('🔍 Looking for draft with ID:', draftId);
      console.log('📋 Available Firebase drafts:', firebaseCVDrafts.map(d => ({ id: d.id, name: d.name })));
      
      const draft = getCVDraftById(draftId);
      console.log('📄 Found draft:', draft);
      console.log('📊 Draft formData:', draft?.formData);
      
      if (!draft && firebaseLoading) {
        // If still loading Firebase data, show a loading message
        showNotification({
          type: 'info',
          title: 'Loading...',
          message: 'Loading CV drafts, please wait...',
          duration: 3000
        });
        return;
      }
      
      if (draft) {
        showNotification({
          type: 'info',
          title: 'Loading CV Draft',
          message: `Opening "${draft.name}" in CV Builder...`,
          duration: 3000
        });
        
        // Check if draft contains formData
        if (draft.formData) {
          // Pass the entire draft object which includes formData
          navigate('/cv-builder', { 
            state: { 
              candidate: draft, // Pass the entire draft object
              mode: 'edit-draft',
              draftId: draftId,
              template: draft.template
            }
          });
          return;
        }
        
        // Fallback: Navigate to CV Builder with draft data mapped as candidate
        // This ensures the auto-fill functionality works properly
        const candidateData = {
          id: draft.candidateId || `draft_${draftId}`,
          _id: draft.candidateId || `draft_${draftId}`,
          name: draft.formData?.fullName || draft.name || 'Draft Candidate',
          fullName: draft.formData?.fullName || draft.name || 'Draft Candidate',
          email: draft.formData?.email || '',
          emailAddress: draft.formData?.email || '',
          phone: draft.formData?.contact || '',
          contactNumber: draft.formData?.contact || '',
          passportNumber: draft.formData?.passportNo || '',
          jobTitle: draft.formData?.position || draft.formData?.workPosition || '',
          salary: draft.formData?.salary || '',
          city: draft.formData?.workCity || '',
          country: draft.formData?.workCountry || '',
          countryApplyingTo: draft.formData?.countryApplyingTo || '',
          stage: draft.formData?.stage || '',
          // Add all the additional fields from draft
          address: draft.formData?.address || '',
          nationality: draft.formData?.nationality || '',
          dateOfBirth: draft.formData?.dateOfBirth || '',
          placeOfBirth: draft.formData?.placeOfBirth || '',
          civilStatus: draft.formData?.civilStatus || '',
          religion: draft.formData?.religion || '',
          height: draft.formData?.height || '',
          weight: draft.formData?.weight || '',
          father: draft.formData?.father || '',
          mother: draft.formData?.mother || '',
          spouse: draft.formData?.spouse || '',
          numberOfKids: draft.formData?.numberOfKids || '',
          educationLevel: draft.formData?.educationLevel || '',
          workPosition: draft.formData?.workPosition || '',
          workYears: draft.formData?.workYears || '',
          workCountry: draft.formData?.workCountry || '',
          englishLevel: draft.formData?.englishLevel || '',
          arabicLevel: draft.formData?.arabicLevel || '',
          allSkills: draft.formData?.allSkills || [],
          age: draft.formData?.age || '',
          gender: draft.formData?.gender || '',
          dateIssued: draft.formData?.dateIssued || '',
          dateExpiry: draft.formData?.dateExpiry || '',
          placeIssued: draft.formData?.placeIssued || '',
          idNumber: draft.formData?.idNumber || '',
          medicalHistory: draft.formData?.medicalHistory || '',
          hobbies: draft.formData?.hobbies || '',
          complexion: draft.formData?.complexion || '',
          notes: draft.formData?.remarks || '',
          emergencyContact: draft.formData?.emergencyContact || '',
          workCompany: draft.formData?.workCompany || ''
        };

        // Navigate to CV Builder with the entire draft object
        // Include both the mapped candidateData and the original draft
        navigate('/cv-builder', { 
          state: { 
            candidate: {
              ...candidateData,
              id: draft.id, // Use the Firebase draft ID
              formData: draft.formData || candidateData // Ensure formData is included
            },
            mode: 'edit-draft',
            draftId: draft.id || draftId,
            template: draft.template || 'template2'
          }
        });
        return;
      } else {
        showNotification({
          type: 'error',
          title: 'Draft Not Found',
          message: 'CV draft not found. It may have been deleted.',
          duration: 5000
        });
        return;
      }
    }
    
    // Special handling for completed CVs
    if (doc.category === 'cv-builder-cvs' && doc.url === '#cv-completed') {
      // Find the document with full data
      const uploadedDocs = JSON.parse(localStorage.getItem('uploadedDocuments') || '[]');
      const cvDoc = uploadedDocs.find((d: any) => d.id === doc.id);
      
      if (cvDoc && cvDoc.formData) {
        showNotification({
          type: 'info',
          title: 'Loading CV',
          message: `Opening "${cvDoc.name}" in CV Builder...`,
          duration: 3000
        });
        
        // Navigate to CV Builder with CV data as candidate
        const candidateData = {
          id: cvDoc.candidateId || `cv_${cvDoc.id}`,
          _id: cvDoc.candidateId || `cv_${cvDoc.id}`,
          name: cvDoc.formData?.fullName || cvDoc.name || 'CV Candidate',
          fullName: cvDoc.formData?.fullName || cvDoc.name || 'CV Candidate',
          email: cvDoc.formData?.email || '',
          emailAddress: cvDoc.formData?.email || '',
          phone: cvDoc.formData?.contact || '',
          contactNumber: cvDoc.formData?.contact || '',
          passportNumber: cvDoc.formData?.passportNo || '',
          jobTitle: cvDoc.formData?.position || cvDoc.formData?.workPosition || '',
          salary: cvDoc.formData?.salary || '',
          city: cvDoc.formData?.workCity || '',
          country: cvDoc.formData?.workCountry || '',
          countryApplyingTo: cvDoc.formData?.countryApplyingTo || '',
          stage: cvDoc.formData?.stage || '',
          // Add all the additional fields from CV
          address: cvDoc.formData?.address || '',
          nationality: cvDoc.formData?.nationality || '',
          dateOfBirth: cvDoc.formData?.dateOfBirth || '',
          placeOfBirth: cvDoc.formData?.placeOfBirth || '',
          civilStatus: cvDoc.formData?.civilStatus || '',
          religion: cvDoc.formData?.religion || '',
          height: cvDoc.formData?.height || '',
          weight: cvDoc.formData?.weight || '',
          father: cvDoc.formData?.father || '',
          mother: cvDoc.formData?.mother || '',
          spouse: cvDoc.formData?.spouse || '',
          numberOfKids: cvDoc.formData?.numberOfKids || '',
          educationLevel: cvDoc.formData?.educationLevel || '',
          workPosition: cvDoc.formData?.workPosition || '',
          workYears: cvDoc.formData?.workYears || '',
          workCountry: cvDoc.formData?.workCountry || '',
          englishLevel: cvDoc.formData?.englishLevel || '',
          arabicLevel: cvDoc.formData?.arabicLevel || '',
          allSkills: cvDoc.formData?.allSkills || [],
          age: cvDoc.formData?.age || '',
          gender: cvDoc.formData?.gender || '',
          dateIssued: cvDoc.formData?.dateIssued || '',
          dateExpiry: cvDoc.formData?.dateExpiry || '',
          placeIssued: cvDoc.formData?.placeIssued || '',
          idNumber: cvDoc.formData?.idNumber || '',
          medicalHistory: cvDoc.formData?.medicalHistory || '',
          hobbies: cvDoc.formData?.hobbies || '',
          complexion: cvDoc.formData?.complexion || '',
          notes: cvDoc.formData?.remarks || '',
          emergencyContact: cvDoc.formData?.emergencyContact || '',
          workCompany: cvDoc.formData?.workCompany || ''
        };

        // Navigate to CV Builder with candidate data
        navigate('/cv-builder', { 
          state: { 
            candidate: candidateData,
            mode: 'view-cv',
            template: cvDoc.template
          }
        });
        return;
      }
    }
    
    // For PDF and image files, show in-app preview
    const fileExtension = doc.name.split('.').pop()?.toLowerCase();
    const isPDF = fileExtension === 'pdf';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(fileExtension || '');
    
    if (isPDF || isImage) {
      setPreviewDocument(doc);
      setShowPreviewModal(true);
    } else if (doc.category !== 'cv-drafts' && doc.category !== 'cv-builder-cvs') {
      // For other document types (not CVs), open in new tab
      window.open(doc.url, '_blank');
    } else {
      // For CV documents that we couldn't process, show an error
      showNotification({
        type: 'error',
        title: 'Unable to Open',
        message: 'Unable to open this CV document. Please try again.',
        duration: 5000
      });
    }
  };

  const handleDownload = (doc: DocFile) => {
    // Special handling for CV documents
    if ((doc.category === 'cv-drafts' || doc.category === 'cv-builder-cvs') && doc.url.startsWith('#')) {
      alert('CV documents can be downloaded from the CV Builder preview.');
      return;
    }
    
    // In a real app, this would download from the server
    const link = document.createElement('a');
    link.href = doc.url;
    link.download = doc.name;
    link.click();
  };

  // Tab component
  const TabButton: React.FC<{
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
  }> = ({ active, onClick, children }) => (
    <button
      onClick={onClick}
      className={cn(
        'px-6 py-3 font-medium text-sm transition-all duration-200 border-b-2',
        active
          ? 'text-[#b8860b] border-[#b8860b] bg-[#f0e4cc] bg-opacity-20'
          : 'text-gray-600 border-transparent hover:text-[#8b6914] hover:border-gray-300'
      )}
    >
      {children}
    </button>
  );

  // Document section component
  const DocumentSection: React.FC<{ section: DocumentSection }> = ({ section }) => {
    const sectionDocuments = section.documents;
    const selectedInSection = sectionDocuments.filter(doc => selectedDocuments.has(doc.id)).length;
    const allSelectedInSection = sectionDocuments.length > 0 && selectedInSection === sectionDocuments.length;
    
    return (
    <Card className={cn(recruitmentTheme.components.card, 'mb-6')} data-section={section.category}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#f0e4cc] rounded-lg">
              {section.icon}
            </div>
            <div>
              <CardTitle className="text-xl text-[#8b6914]">{section.title}</CardTitle>
              <CardDescription>{section.description}</CardDescription>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {sectionDocuments.length > 0 && (
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => handleSelectAll(sectionDocuments)}
                  variant="outline"
                  size="sm"
                  className="flex items-center space-x-1"
                >
                  {allSelectedInSection ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                  <span>{allSelectedInSection ? `Deselect All` : `Select All`}</span>
                </Button>
                {selectedInSection > 0 && (
                  <span className="text-sm text-[#8b6914] font-medium">
                    {selectedInSection} selected
                  </span>
                )}
              </div>
            )}
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => handleFileUpload(section.category)}
                className="bg-white hover:bg-gray-50 text-[#8b6914] border-2 border-[#f0e4cc] rounded-xl px-4 py-3 font-semibold transition-all duration-200 flex items-center space-x-2"
                data-upload-button={section.category}
                style={{ color: '#8b6914' }}
              >
                <Upload className="w-4 h-4" style={{ color: '#8b6914' }} />
                <span style={{ color: '#8b6914' }}>Upload</span>
              </Button>
              <Button
                onClick={() => handleCameraCapture(section.category)}
                className="bg-blue-50 hover:bg-blue-100 text-blue-600 border-2 border-blue-200 rounded-xl px-4 py-3 font-semibold transition-all duration-200 flex items-center space-x-2"
                title="Take Photo"
              >
                <Camera className="w-4 h-4 text-blue-600" />
                <span className="text-blue-600">Camera</span>
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {section.documents.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Folder className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No documents uploaded yet</p>
            <p className="text-sm mt-1">Click the upload button to add documents</p>
          </div>
        ) : (
          <div className="space-y-3">
            {section.documents.map((doc, index) => (
              <div
                key={doc.id}
                className={cn(
                  "flex items-center justify-between p-4 rounded-lg transition-colors duration-200",
                  selectedDocuments.has(doc.id) 
                    ? "bg-[#f0e4cc] border-2 border-[#8b6914]" 
                    : "bg-gray-50 hover:bg-gray-100"
                )}
              >
                <div className="flex items-center space-x-3">
                  <Button
                    onClick={() => handleSelectDocument(doc.id)}
                    variant="ghost"
                    size="icon"
                    className="p-1 hover:bg-[#f0e4cc]"
                  >
                    {selectedDocuments.has(doc.id) ? (
                      <CheckSquare className="w-5 h-5 text-[#8b6914]" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400" />
                    )}
                  </Button>
                  <span className="font-bold text-[#8b6914] bg-[#f0e4cc] px-2 py-1 rounded text-sm min-w-[2rem] text-center">
                    {index + 1}.
                  </span>
                  {getFileIcon(doc.type)}
                  <div className="flex-1">
                    {editingDocument && editingDocument.id === doc.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={editingDocument.name}
                          onChange={(e) => setEditingDocument({ ...editingDocument, name: e.target.value })}
                          className="font-medium text-gray-900 bg-white border border-gray-300 rounded px-2 py-1 flex-1"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                          autoFocus
                        />
                        <Button
                          onClick={handleSaveEdit}
                          size="icon"
                          variant="ghost"
                          className="p-1 hover:bg-green-100 hover:text-green-600"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={handleCancelEdit}
                          size="icon"
                          variant="ghost"
                          className="p-1 hover:bg-red-100 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <p className="font-medium text-gray-900">{doc.name}</p>
                    )}
                    <p className="text-sm text-gray-500">
                      {formatFileSize(doc.size)} • Uploaded {formatDateTime(doc.uploadedAt)} by {doc.uploadedBy}
                    </p>
                    {doc.description && (
                      <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handlePreview(doc)}
                    className="hover:bg-[#f0e4cc] hover:text-[#8b6914]"
                    title="Preview"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleStartEdit(doc)}
                    className="hover:bg-[#f0e4cc] hover:text-[#8b6914]"
                    title="Edit/Rename"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleDownload(doc)}
                    className="hover:bg-[#f0e4cc] hover:text-[#8b6914]"
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleDelete(doc.id)}
                    className="hover:bg-red-100 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
    );
  };

  return (
    <div className={themeClasses.pageContainer}>
      <div className={themeClasses.contentContainer}>
        {/* Page Header */}
        <div className="mb-8">
          <h1 className={themeClasses.pageTitle}>Documents</h1>
          <p className={themeClasses.pageSubtitle}>
            Manage contracts, licenses, certifications, and other important documents
          </p>
        </div>

        {/* Bulk Actions Bar */}
        {selectedDocuments.size > 0 && (
          <div className="mb-6 p-4 bg-[#f0e4cc] border border-[#8b6914] rounded-lg flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="font-medium text-[#8b6914]">
                {selectedDocuments.size} document{selectedDocuments.size !== 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={handleBulkDelete}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50 flex items-center space-x-2"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete Selected</span>
              </Button>
              <Button
                onClick={clearSelection}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <X className="w-4 h-4" />
                <span>Clear Selection</span>
              </Button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <div className="flex space-x-8 overflow-x-auto">
            <TabButton
              active={activeTab === 'cvs'}
              onClick={() => setActiveTab('cvs')}
            >
              CVs
            </TabButton>
            <TabButton
              active={activeTab === 'medical-reports'}
              onClick={() => setActiveTab('medical-reports')}
            >
              Medical Reports
            </TabButton>
            <TabButton
              active={activeTab === 'contracts'}
              onClick={() => setActiveTab('contracts')}
            >
              Contracts
            </TabButton>
            <TabButton
              active={activeTab === 'licenses'}
              onClick={() => setActiveTab('licenses')}
            >
              Licenses & Certifications
            </TabButton>
            <TabButton
              active={activeTab === 'marketing'}
              onClick={() => setActiveTab('marketing')}
            >
              Adverts/Marketing
            </TabButton>
            <TabButton
              active={activeTab === 'reports'}
              onClick={() => setActiveTab('reports')}
            >
              Reports
            </TabButton>
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'medical-reports' ? (
            <div>
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-lg font-medium text-blue-900 mb-2">Medical Reports Hub</h3>
                <p className="text-blue-700">
                  Manage medical examination reports, health certificates, vaccination records, and other health-related documents. 
                  Ensure compliance with employment health requirements and maintain organized medical records for all candidates.
                </p>
              </div>
              {medicalReportsSections.map(section => (
                <DocumentSection key={section.category} section={section} />
              ))}
            </div>
          ) : activeTab === 'cvs' ? (
            <div>
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-blue-900 mb-2">CV Builder Integration</h3>
                    <p className="text-blue-700">
                      CVs created in the CV Builder are automatically saved here. You can also upload external CVs manually.
                      All CVs are linked to their respective candidates for easy access and management.
                    </p>
                  </div>
                  <Button
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to delete ALL CV drafts? This action cannot be undone and will reload the page.')) {
                        try {
                          showNotification({
                            type: 'info',
                            title: 'Cleaning up...',
                            message: 'Removing all CV drafts. The page will reload.',
                            duration: 3000
                          });
                          
                          const result = await comprehensiveCVDraftCleanup();
                          
                          if (!result.success) {
                            throw new Error('Cleanup failed');
                          }
                        } catch (error) {
                          console.error('Cleanup error:', error);
                          showNotification({
                            type: 'error',
                            title: 'Clear Failed',
                            message: 'Failed to clear CV drafts. Check console for details.',
                            duration: 5000
                          });
                        }
                      }
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded ml-4"
                  >
                    Clear All CV Drafts
                  </Button>
                </div>
              </div>
              {cvSections.map(section => (
                <DocumentSection key={section.category} section={section} />
              ))}
            </div>
          ) : activeTab === 'contracts' ? (
            <div>
              {contractSections.map(section => (
                <DocumentSection key={section.category} section={section} />
              ))}
            </div>
          ) : activeTab === 'licenses' ? (
            <div>
              {licenseSections.map(section => (
                <DocumentSection key={section.category} section={section} />
              ))}
            </div>
          ) : activeTab === 'marketing' ? (
            <div>
              <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <h3 className="text-lg font-medium text-orange-900 mb-2">Marketing & Advertising Hub</h3>
                <p className="text-orange-700">
                  Centralize all your marketing materials, job advertisements, brand assets, and social media content.
                  Keep track of promotional campaigns and maintain consistent branding across all recruitment efforts.
                </p>
              </div>
              {marketingSections.map(section => (
                <DocumentSection key={section.category} section={section} />
              ))}
            </div>
          ) : (
            <div>
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="text-lg font-medium text-green-900 mb-2">Reports & Analytics</h3>
                <p className="text-green-700">
                  Store and manage all business reports including financial statements, performance metrics, 
                  compliance documents, and analytics reports. Maintain organized records for audits and strategic planning.
                </p>
              </div>
              {reportsSections.map(section => (
                <DocumentSection key={section.category} section={section} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* PDF/Image Preview Modal */}
      {showPreviewModal && previewDocument && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowPreviewModal(false);
              setPreviewDocument(null);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-6xl max-h-[90vh] w-[90vw] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-[#8b6914]">
                {previewDocument.name}
              </h3>
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => handleDownload(previewDocument)}
                  variant="ghost"
                  size="icon"
                  className="hover:bg-[#f0e4cc] hover:text-[#8b6914]"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => {
                    setShowPreviewModal(false);
                    setPreviewDocument(null);
                  }}
                  variant="ghost"
                  size="icon"
                  className="hover:bg-red-100 hover:text-red-600"
                  title="Close"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 p-4 overflow-auto">
              {(() => {
                const fileExtension = previewDocument.name.split('.').pop()?.toLowerCase();
                const isPDF = fileExtension === 'pdf';
                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(fileExtension || '');

                if (isPDF) {
                  return (
                    <div className="w-full h-full min-h-[70vh] bg-gray-100 rounded">
                      <iframe
                        src={previewDocument.url}
                        className="w-full h-full min-h-[70vh] border-0 rounded"
                        title={`PDF Preview: ${previewDocument.name}`}
                      />
                    </div>
                  );
                } else if (isImage) {
                  return (
                    <div className="flex items-center justify-center min-h-[70vh]">
                      <img
                        src={previewDocument.url}
                        alt={previewDocument.name}
                        className="max-w-full max-h-full object-contain rounded shadow-lg"
                        style={{ maxHeight: 'calc(90vh - 200px)' }}
                      />
                    </div>
                  );
                } else {
                  return (
                    <div className="flex items-center justify-center min-h-[70vh] text-gray-500">
                      <div className="text-center">
                        <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                        <p>Preview not available for this file type.</p>
                        <Button
                          onClick={() => handleDownload(previewDocument)}
                          className="mt-4"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download File
                        </Button>
                      </div>
                    </div>
                  );
                }
              })()}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <div>
                  <span className="font-medium">Size:</span> {(previewDocument.size / 1024).toFixed(1)} KB
                </div>
                <div>
                  <span className="font-medium">Uploaded:</span> {formatDateTime(previewDocument.uploadedAt)}
                </div>
                <div>
                  <span className="font-medium">Type:</span> {previewDocument.name.split('.').pop()?.toUpperCase()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;